/**
 * Data access for the Platform page: GET /api/observability/* (see contract.js).
 *
 * A response that answers with the wrong shape is an ERROR, never an empty view: on a
 * page whose job is to say what is true, a regressed route must read as broken rather
 * than as a quiet platform. A source the backend could not read is different, and is
 * not an error here: the response still arrives and names it in `warnings`.
 */
import { apiFetch } from '../api';
import { ENDPOINTS } from './contract';

async function load(url, isValid) {
  const res = await apiFetch(url);
  const path = url.split('?')[0];
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const why = typeof body?.detail === 'string' ? `: ${body.detail}` : '';
    throw new Error(`${path} answered HTTP ${res.status}${why}`);
  }
  const body = await res.json().catch(() => null);
  if (!isValid(body)) throw new Error(`${path} returned an unexpected response`);
  return body;
}

const hasSeries = (b) => Boolean(b && Array.isArray(b.timestamps) && b.series && typeof b.series === 'object');

export const loadOverview = range => load(`${ENDPOINTS.overview}?range=${range}`,
  b => Array.isArray(b?.services));

export const loadServiceMetrics = (serviceId, range) => load(
  `${ENDPOINTS.serviceMetrics(serviceId)}?range=${range}`,
  b => Boolean(b && Array.isArray(b.replicas) && b.metrics && typeof b.metrics === 'object'));

export const loadKubernetes = range => load(`${ENDPOINTS.kubernetes}?range=${range}`, hasSeries);

export const loadDatabase = range => load(`${ENDPOINTS.database}?range=${range}`, hasSeries);

export const loadEdge = range => load(`${ENDPOINTS.edge}?range=${range}`, hasSeries);

// The largest workspaces, descending (services/workspace_usage.py). `fsId` narrows it to
// one shard; the backend caps `limit` at 500.
export const loadWorkspaces = ({ fsId, limit = 100 } = {}) => load(
  `${ENDPOINTS.workspaces}?limit=${limit}${fsId ? `&fs_id=${encodeURIComponent(fsId)}` : ''}`,
  b => Array.isArray(b?.users) && typeof b?.total_bytes === 'number');

export const loadDeployments = () => load(ENDPOINTS.deployments,
  b => Array.isArray(b?.images) && Array.isArray(b?.history));

export const loadJobs = () => load(ENDPOINTS.jobs, b => Array.isArray(b?.jobs));

export const loadJobRuns = (key, { limit = 20, before } = {}) => load(
  `${ENDPOINTS.jobRuns(key)}?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ''}`,
  b => Array.isArray(b?.runs));

export const loadAlarms = () => load(ENDPOINTS.alarms, b => Array.isArray(b?.alarms));
